/* eslint-disable default-case */
export const GoalSource = {
  UNCOMPLETED: 'uncompleted',
  COMPLETED: 'completed',
  ALL: 'all',
};

export const ItemSource = {
  REVIEW: 'review',
  NEW: 'new',
  ALL: 'all',
};

/**
 * Model for individual session config.
 */
export class SessionConfig {
  /**
   * Constructor
   *
   * @param {Object} properties
   */
  constructor(properties = {}) {
    // Set up the ability to disable attributes.
    this._disabledValues = {
      itemSource: new Set(),
      goalSource: new Set(),
    };

    _.defaults(this, properties);
  }

  /**
   * Disables a value for a particular property.
   *
   * @param {String} [itemSource|goalSource] property
   * @param {String|Number} value
   * @returns {Boolean}     returns true if current selection is unaffected
   */
  disable(property, value) {
    this._disabledValues[property].add(value);

    // Set the current value to the default if it is selected.
    if (this[property] === value) {
      this._setToDefault(property);
      return false;
    }

    return true;
  }

  /**
   * Enables a value for a particular property.
   *
   * @param {String} [itemSource|goalSource] property
   * @param {String|Number} value
   * @returns {Boolean}     returns true if current selection is unaffected
   */
  enable(property, value) {
    this._disabledValues[property].delete(value);
    return true;
  }

  /**
   * Determines if a property and corresponding value are
   * disabled.
   *
   * @param {String} [itemSource|goalSource] property
   * @param {String|Number} value
   * @returns {Boolean}
   */
  isDisabled(property, value) {
    const disabledValues = this._disabledValues[property];
    return disabledValues && disabledValues.has(value);
  }

  /**
   * Returns true if any properties are disabled.
   *
   * @returns {Boolean}
   */
  hasDisabledProperties() {
    return this._disabledValues.itemSource.size > 0 || this._disabledValues.goalSource.size > 0;
  }

  /**
   * Takes a property and moves it to the fallback value.
   *
   * @param {String} property
   */
  _setToDefault(property) {
    switch (property) {
      case 'itemSource':
        this.itemSource = ItemSource.ALL;
        break;
      case 'goalSource':
        this.goalSource = GoalSource.ALL;
        break;
    }
  }

  /**
   * Enables a value for a particular property.
   *
   * @param {String} [itemSource|goalSource] property
   * @param {String|Number} value
   * @param {Boolean} disable     whether to disable the property
   * @returns {Boolean}           returns true if current selection is unaffected
   */
  toggleDisabled(property, value, disable = false) {
    return disable ? this.disable(property, value) : this.enable(property, value);
  }

  /**
   * Updates the session configuration. If some properties are disabled,
   * will set to default.
   *
   * @param {Object} properties
   * @returns {Boolean}   true if nothing was set to the fallback value.
   */
  update(properties = {}) {
    let updatedSuccessfully = true;

    _.extend(this, properties);

    // Set the defaults to
    Object.keys(this).forEach(property => {
      if (this.isDisabled(property, this[property])) {
        this._setToDefault(property);
        updatedSuccessfully = false;
      }
    });

    return updatedSuccessfully;
  }
}

export default SessionConfig;
